import { clamp01 } from '../math/MathUtils';
import { type Unsubscribe } from '../utils/Unsubscribe';
import { AudioBus, type AudioBackend, type AudioClip, type AudioPlayback, type PlaybackRequest } from './AudioBackend';
import { SilentAudioBackend } from './SilentAudioBackend';
import { isWebAudioAvailable, WebAudioBackend } from './WebAudioBackend';

const DEFAULT_VOLUME = 1;
const DEFAULT_RATE = 1;
const MUTED_GAIN = 0;

/** Events that count as the gesture a browser wants before it will let audio start. */
const POINTER_UNLOCK_EVENT = 'pointerdown';
const KEYBOARD_UNLOCK_EVENT = 'keydown';

/** Where {@link AudioManager} looks up a clip by asset key. */
export interface AudioClipProvider {
  /** The clip loaded under this key, or undefined when nothing was loaded for it. */
  getAudio(key: string): AudioClip | undefined;
}

/** What a page element must offer for {@link AudioManager.attachUnlockGesture} to use it. */
export interface GestureTarget {
  /** Subscribes to a DOM event. */
  addEventListener(type: string, listener: () => void): void;
  /** Cancels a subscription made with {@link GestureTarget.addEventListener}. */
  removeEventListener(type: string, listener: () => void): void;
}

/** Construction options for {@link AudioManager}. */
export interface AudioManagerOptions {
  /** Overrides the backend. Defaults to Web Audio, or silence where Web Audio does not exist. */
  readonly backend?: AudioBackend | undefined;
  /** Where asset keys are resolved to clips; normally the `AssetManager`. */
  readonly clips?: AudioClipProvider | undefined;
}

/** How one call to {@link AudioManager.play} differs from the defaults. */
export interface PlayOptions {
  /** Bus to play on. Defaults to {@link AudioBus.Sfx}. */
  readonly bus?: AudioBus;
  /** Per-sound gain from zero to one. Defaults to one. */
  readonly volume?: number;
  /** Whether the sound repeats until stopped. Defaults to false. */
  readonly loop?: boolean;
  /** Playback rate, where one is the recorded speed. Defaults to one. */
  readonly rate?: number;
}

/** The mutable level of one bus. */
interface BusLevel {
  volume: number;
  muted: boolean;
}

/**
 * Plays sounds by asset key, and owns the volume policy above the backend.
 *
 * Games call `context.audio.play('click')` and never see a decoded clip, a gain node or the question of
 * whether the browser has allowed audio yet. Volume and mute live here rather than in the backend
 * because they are a product decision that must survive swapping the backend out.
 *
 * A sound whose key was never loaded is skipped rather than thrown on. A missing bark should not end a
 * toddler's play session, and the debug layer is where a missing key is worth surfacing.
 */
export class AudioManager {
  readonly #backend: AudioBackend;
  readonly #levels = new Map<AudioBus, BusLevel>([
    [AudioBus.Master, { volume: DEFAULT_VOLUME, muted: false }],
    [AudioBus.Sfx, { volume: DEFAULT_VOLUME, muted: false }],
    [AudioBus.Music, { volume: DEFAULT_VOLUME, muted: false }],
  ]);

  #clips: AudioClipProvider | undefined;
  #disposed = false;

  constructor(options: AudioManagerOptions = {}) {
    this.#backend = options.backend ?? (isWebAudioAvailable() ? new WebAudioBackend() : new SilentAudioBackend());
    this.#clips = options.clips;

    for (const bus of this.#levels.keys()) {
      this.#applyGain(bus);
    }
  }

  /** Whether the browser has let audio start. Sounds played before this are silently dropped. */
  get isUnlocked(): boolean {
    return this.#backend.isUnlocked;
  }

  /**
   * Points the manager at where clips are loaded.
   *
   * Exists because the asset manager decodes through the same backend this manager plays through, so
   * one of the two has to be wired up after the other is built.
   */
  setClipProvider(clips: AudioClipProvider): void {
    this.#clips = clips;
  }

  /** Starts audio output. Must be reached from a user gesture; calling it twice is harmless. */
  async unlock(): Promise<void> {
    await this.#backend.unlock();
  }

  /**
   * Unlocks audio the first time the user touches the pointer target or types on the keyboard target.
   *
   * The two targets are separate because a canvas only hears the pointer: keyboard events go to
   * whatever holds focus, normally `window`, so listening for a key on the canvas would be listening
   * for something that never arrives. Omitting the keyboard target leaves the pointer as the only way
   * in, which is right for a host whose canvas is the whole interaction.
   *
   * The listeners stay until a gesture actually unlocks audio, rather than being spent on the first
   * one that arrives: a browser may refuse the first resume, and a one-shot listener would leave the
   * page permanently silent. Returns the function that removes them early, for a host that tears down
   * before any gesture.
   */
  attachUnlockGesture(pointerTarget: GestureTarget, keyboardTarget?: GestureTarget): Unsubscribe {
    const detachers: Unsubscribe[] = [];

    const detach = (): void => {
      for (const remove of detachers) {
        remove();
      }
      detachers.length = 0;
    };

    const listener = (): void => {
      void this.unlock().then(() => {
        if (this.isUnlocked) {
          detach();
        }
      });
    };

    for (const [target, type] of toUnlockBindings(pointerTarget, keyboardTarget)) {
      target.addEventListener(type, listener);
      detachers.push(() => {
        target.removeEventListener(type, listener);
      });
    }

    return detach;
  }

  /**
   * Turns encoded audio bytes into a playable clip.
   *
   * The asset layer calls this rather than talking to a backend, so decoding and playback can never
   * disagree about what a clip is.
   */
  async decode(bytes: ArrayBuffer): Promise<AudioClip> {
    return this.#backend.decode(bytes);
  }

  /** Plays the clip loaded under `key`, or does nothing when no clip was loaded for it. */
  play(key: string, options: PlayOptions = {}): AudioPlayback | undefined {
    const clip = this.#clips?.getAudio(key);
    if (clip === undefined) {
      return undefined;
    }
    return this.playClip(clip, options);
  }

  /** Plays a clip the caller already holds, for sounds produced outside the asset layer. */
  playClip(clip: AudioClip, options: PlayOptions = {}): AudioPlayback {
    return this.#backend.play(clip, toPlaybackRequest(options));
  }

  /** Current volume of a bus, from zero to one, ignoring its mute state. */
  getVolume(bus: AudioBus): number {
    return this.#levels.get(bus)?.volume ?? DEFAULT_VOLUME;
  }

  /** Sets a bus' volume, clamped to zero to one. Muting is tracked separately. */
  setVolume(bus: AudioBus, volume: number): void {
    const level = this.#levels.get(bus);
    if (level === undefined) {
      return;
    }
    level.volume = clamp01(volume);
    this.#applyGain(bus);
  }

  /** Whether a bus is muted. */
  isMuted(bus: AudioBus): boolean {
    return this.#levels.get(bus)?.muted ?? false;
  }

  /** Mutes or unmutes a bus without disturbing the volume it will return to. */
  setMuted(bus: AudioBus, muted: boolean): void {
    const level = this.#levels.get(bus);
    if (level === undefined) {
      return;
    }
    level.muted = muted;
    this.#applyGain(bus);
  }

  /** Stops every sound currently playing, on every bus. */
  stopAll(): void {
    this.#backend.stopAll();
  }

  /** Stops everything and releases the backend. */
  dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;
    this.#backend.dispose();
  }

  #applyGain(bus: AudioBus): void {
    const level = this.#levels.get(bus);
    if (level === undefined) {
      return;
    }
    this.#backend.setBusGain(bus, level.muted ? MUTED_GAIN : level.volume);
  }
}

/** Which event to listen for on which target, given the targets a host offered. */
function toUnlockBindings(
  pointerTarget: GestureTarget,
  keyboardTarget: GestureTarget | undefined,
): readonly (readonly [GestureTarget, string])[] {
  if (keyboardTarget === undefined) {
    return [[pointerTarget, POINTER_UNLOCK_EVENT]];
  }
  return [
    [pointerTarget, POINTER_UNLOCK_EVENT],
    [keyboardTarget, KEYBOARD_UNLOCK_EVENT],
  ];
}

function toPlaybackRequest(options: PlayOptions): PlaybackRequest {
  return {
    bus: options.bus ?? AudioBus.Sfx,
    volume: clamp01(options.volume ?? DEFAULT_VOLUME),
    loop: options.loop ?? false,
    rate: options.rate ?? DEFAULT_RATE,
  };
}
